import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree, useLoader } from "@react-three/fiber";
import { OrbitControls, Grid, PerspectiveCamera, OrthographicCamera, Html, Line, SoftShadows, ContactShadows, GizmoHelper, GizmoViewcube, Environment, Lightformer } from "@react-three/drei";
import * as THREE from "three";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { ModuleMesh, ACCENT } from "./ModuleMesh";
import { SiteGround } from "./SiteGround";
import { Surroundings, Terrain } from "./SiteDressing";
import { RigEffects } from "./RigEffects";
import { PhotoMapsProvider, assets as manifest } from "./rigAssets";
import { resolveSky, sunDirection, DEFAULT_SKY, effectiveQuality } from "./rigSky";
import { DELIVERY_COLORS, SNAP_TOLERANCE, bounds as boundsOf, footprint, snapPosition, r3, dragState, shade, nextLabel, connectionPoints, worldSide, rect, floorSpan } from "./rigGeometry";

export const SCENE_BG = "#eef0f3";
const PRESET_DIRS = {
  iso: new THREE.Vector3(0.6, 0.68, 0.78),
  top: new THREE.Vector3(0.0001, 1, 0.0001),
  front: new THREE.Vector3(0, 0.25, 1),
  right: new THREE.Vector3(1, 0.25, 0),
};
const fmt1 = (v) => Number(v).toLocaleString("nb-NO", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/* Frames the rig (or a subset of module ids) whenever `fit.key`, the view or the default camera changes – but only once
   the canvas has a real size, so the very first frame is already correct. */
function CameraRig({ fit, view, modules, types, floorHeight, controlsRef }) {
  const { camera, size: vp } = useThree();
  const done = useRef({ key: -1, view: null, cam: null, w: 0, h: 0 });
  const interacted = useRef(false);
  // remember whether the user has orbited since the last fit – a resize only re-frames an untouched view
  useEffect(() => {
    const c = controlsRef.current;
    if (!c) return;
    const onStart = () => { interacted.current = true; };
    c.addEventListener("start", onStart);
    return () => c.removeEventListener("start", onStart);
  }, [controlsRef, view]);
  useEffect(() => {
    if (vp.width < 40 || vp.height < 40) return;
    const d = done.current;
    const same = d.key === fit.key && d.view === view && d.cam === camera;
    const resized = Math.abs(d.w - vp.width) > 2 || Math.abs(d.h - vp.height) > 2;
    if (same && (!resized || interacted.current)) return;
    done.current = { key: fit.key, view, cam: camera, w: vp.width, h: vp.height };
    interacted.current = false;
    const subset = fit.ids ? modules.filter((m) => fit.ids.includes(m.id)) : modules;
    const b = boundsOf(subset.length ? subset : modules, types);
    const ex = Math.max(8, b.x1 - b.x0);
    const ez = Math.max(8, b.z1 - b.z0);
    const ey = Math.max(3, b.floors * floorHeight);
    const cx = b.cx, cz = b.cz, cy = Math.min(ey / 2, 4);
    const c = controlsRef.current;
    if (view === "2D") {
      camera.up.set(0, 0, -1);
      camera.position.set(cx, 250, cz);
      camera.zoom = Math.max(1.2, Math.min(vp.width / (ex * 1.3), vp.height / (ez * 1.3)));
      camera.lookAt(cx, 0, cz);
      camera.updateProjectionMatrix();
      if (c) { c.target.set(cx, 0, cz); c.update(); }
      return;
    }
    camera.up.set(0, 1, 0);
    const dir = (PRESET_DIRS[fit.preset] || PRESET_DIRS.iso).clone().normalize();
    const r = 0.5 * Math.hypot(ex, ey, ez);
    const vfov = THREE.MathUtils.degToRad(camera.fov || 38);
    const aspect = vp.width / Math.max(1, vp.height);
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * aspect);
    const dist = Math.max(12, (r / Math.sin(Math.min(vfov, hfov) / 2)) * 0.8);
    camera.position.set(cx, cy, cz).add(dir.multiplyScalar(dist));
    camera.lookAt(cx, cy, cz);
    camera.updateProjectionMatrix();
    if (c) { c.target.set(cx, cy, cz); c.update(); }
  }, [fit, view, camera, vp.width, vp.height, modules, types, floorHeight, controlsRef]);
  return null;
}

/* Exposes screen → ground-plane projection and a still-image capture to the DOM layer (library drag & drop, placement
   mode, «Ta bilde»). `sceneApi` (optional) receives the same object so the page can call it. */
function SceneApi({ apiRef, sceneApi, requestCapture }) {
  const { camera, gl } = useThree();
  useEffect(() => {
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const hit = new THREE.Vector3();
    const api = {
      toGround(clientX, clientY, y = 0) {
        const rect = gl.domElement.getBoundingClientRect();
        ndc.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
        raycaster.setFromCamera(ndc, camera);
        const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -y);
        return raycaster.ray.intersectPlane(plane, hit) ? { x: hit.x, z: hit.z } : null;
      },
      /* Resolves with a PNG data URL of the scene without editor chrome (view cube, grid). */
      capture: requestCapture,
    };
    apiRef.current = api;
    if (sceneApi) sceneApi.current = api;
    return () => { if (sceneApi && sceneApi.current === api) sceneApi.current = null; };
  }, [camera, gl, apiRef, sceneApi, requestCapture]);
  return null;
}

/* Runs once the scene has been committed in «photo» state: renders one frame synchronously (the canvas is on-demand and
   does not preserve its buffer) and reads it back before the compositor gets to it. */
function CaptureRunner({ shooting, shotRef, onDone }) {
  const { gl, advance } = useThree();
  useEffect(() => {
    if (!shooting) return;
    const s = shotRef.current;
    shotRef.current = null;
    try {
      advance(performance.now(), true);
      s?.resolve(gl.domElement.toDataURL("image/png"));
    } catch (e) {
      s?.reject(e);
    }
    onDone();
  }, [shooting, shotRef, onDone, gl, advance]);
  return null;
}

/* Window-level pointer tracking while a module is being dragged inside the scene. */
function DragController({ dragRef, onMove, onEnd }) {
  const { camera, gl } = useThree();
  useEffect(() => {
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const hit = new THREE.Vector3();
    const move = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const rect = gl.domElement.getBoundingClientRect();
      ndc.set(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      if (raycaster.ray.intersectPlane(d.plane, hit)) onMove(hit, e);
    };
    const up = () => { if (dragRef.current) onEnd(); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, [camera, gl, dragRef, onMove, onEnd]);
  return null;
}

/* Reports the on-screen length of 10 m at the rig centre (scale bar) and the camera heading (north arrow). */
function ScaleProbe({ center, onMeasure }) {
  const { camera, size } = useThree();
  const last = useRef({ px: 0, heading: 0 });
  const a = useMemo(() => new THREE.Vector3(), []);
  const b = useMemo(() => new THREE.Vector3(), []);
  const dir = useMemo(() => new THREE.Vector3(), []);
  useFrame(() => {
    a.set(center[0], 0, center[2]).project(camera);
    b.set(center[0] + 10, 0, center[2]).project(camera);
    const px = Math.hypot(((b.x - a.x) * size.width) / 2, ((b.y - a.y) * size.height) / 2);
    camera.getWorldDirection(dir);
    const heading = Math.atan2(dir.x, -dir.z); // 0 = looking north (-z)
    if (Math.abs(px - last.current.px) > 0.5 || Math.abs(heading - last.current.heading) > 0.01) {
      last.current = { px, heading };
      onMeasure(px, heading);
    }
  });
  return null;
}

/* Sun: a directional light placed astronomically (see rigSky.js) with a shadow frustum sized to the rig. */
function SunLight({ center, size, shadows = true, sky, mapSize = 2048 }) {
  const ref = useRef();
  const { scene } = useThree();
  const [cx, , cz] = center;
  useEffect(() => {
    const l = ref.current;
    if (!l) return;
    l.target.position.set(cx, 0, cz);
    scene.add(l.target);
    const s = Math.max(28, size * 0.8);
    const cam = l.shadow.camera;
    cam.left = -s; cam.right = s; cam.top = s; cam.bottom = -s; cam.near = 1; cam.far = 520;
    cam.updateProjectionMatrix();
    l.shadow.needsUpdate = true;
    return () => { scene.remove(l.target); };
  }, [cx, cz, size, scene]);
  const el = Math.max(sky.isNight ? 35 : 4, sky.sun.elevation); // keep shadows readable when the sun is very low; moonlight at night
  const [dx, dy, dz] = sunDirection(sky.isNight ? (sky.sun.azimuth + 140) % 360 : sky.sun.azimuth, el);
  const dist = 160;
  return <directionalLight ref={ref} position={[cx + dx * dist, dy * dist, cz + dz * dist]} intensity={sky.sunIntensity} color={sky.sunColor} castShadow={shadows && sky.sunIntensity > 0.05} shadow-mapSize={[mapSize, mapSize]} shadow-bias={-0.0002} shadow-normalBias={0.03} />;
}

/*
 * Fallback image-based light without downloads: a virtual studio (bright dome, warm sun card, cool fill, darker
 * ground card) rendered once into a cube map. Used while the HDRI sky is loading and if it fails to load.
 * Memoised with no props so the cube map is never re-rendered while the user drags modules.
 */
const StudioEnvironment = React.memo(function StudioEnvironment() {
  return (
    <Environment resolution={256} frames={1} environmentIntensity={0.85}>
      <mesh scale={120}>
        <sphereGeometry args={[1, 32, 16]} />
        <meshBasicMaterial color="#b9c6d4" side={THREE.BackSide} toneMapped={false} />
      </mesh>
      <Lightformer form="ring" intensity={2.4} color="#e9f0f8" scale={[80, 80, 1]} position={[0, 60, 0]} rotation={[Math.PI / 2, 0, 0]} />
      <Lightformer form="rect" intensity={3.2} color="#fff3e2" scale={[26, 12, 1]} position={[-28, 40, 48]} target={[0, 0, 0]} />
      <Lightformer form="rect" intensity={1.1} color="#d7e3f3" scale={[40, 14, 1]} position={[50, 22, -30]} target={[0, 0, 0]} />
      <Lightformer form="rect" intensity={0.55} color="#7d8791" scale={[200, 200, 1]} position={[0, -30, 0]} rotation={[-Math.PI / 2, 0, 0]} />
    </Environment>
  );
});

class EnvBoundary extends React.Component {
  constructor(p) { super(p); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.warn("HDRI-himmel kunne ikke lastes – bruker studio-lys", err?.message || err); }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

/* Real sky: vendored CC0 HDRI (assets3d) as environment + background, rotated so its sun matches the computed sun.
   While a new HDRI downloads the previously shown one stays (no flash of studio light); the first load falls back to
   the studio. Once the first sky is up, the remaining 1k skies are prefetched in the background so later switches of
   time/weather are instant. */
function LoadedProbe({ file, onLoaded }) {
  useEffect(() => { onLoaded(file); }, [file, onLoaded]);
  return null;
}
function SkyEnvironment({ sky, background }) {
  const file = sky.hdri?.file;
  const [shown, setShown] = useState(null); // last HDRI that finished loading
  const onLoaded = useCallback((f) => setShown(f), []);
  useEffect(() => {
    if (!shown || typeof window === "undefined" || window.innerWidth < 1024) return undefined;
    const t = setTimeout(() => {
      manifest.hdri.filter((h) => h.file !== shown && !h.file.includes("_2k")).forEach((h) => { try { useLoader.preload(RGBELoader, h.file); } catch { /* ignore */ } });
    }, 6000);
    return () => clearTimeout(t);
  }, [shown]);
  if (!file) return <StudioEnvironment />;
  const envProps = { background, backgroundBlurriness: 0.02, backgroundIntensity: sky.backgroundIntensity, environmentIntensity: sky.envIntensity, environmentRotation: [0, sky.rotationY, 0], backgroundRotation: [0, sky.rotationY, 0] };
  const fallback = shown && shown !== file ? <Environment files={shown} {...envProps} /> : <StudioEnvironment />;
  return (
    <EnvBoundary fallback={<StudioEnvironment />}>
      <Suspense fallback={fallback}>
        <Environment files={file} {...envProps} />
        <LoadedProbe file={file} onLoaded={onLoaded} />
      </Suspense>
    </EnvBoundary>
  );
}

/* Applies exposure to the renderer (used by both the plain ACES path and the post-processing ToneMapping pass). */
function Exposure({ value }) {
  const { gl, invalidate } = useThree();
  useEffect(() => { gl.toneMappingExposure = value; invalidate(); }, [gl, value, invalidate]);
  return null;
}

function Lights({ center, size, shadows, sky, background, mapSize }) {
  return (
    <>
      <hemisphereLight args={[sky.isNight ? "#6d7a94" : "#ffffff", "#8e969f", sky.isNight ? 0.08 : 0.16]} />
      <SunLight center={center} size={size} shadows={shadows} sky={sky} mapSize={mapSize} />
      <SkyEnvironment sky={sky} background={background} />
      <Exposure value={sky.exposure} />
    </>
  );
}

/* Plan-view snap guide: a thin dashed accent line on the active floor plane. */
function SnapGuide({ g, y }) {
  const points = g.axis === "x" ? [[g.at, y + 0.03, g.from], [g.at, y + 0.03, g.to]] : [[g.from, y + 0.03, g.at], [g.to, y + 0.03, g.at]];
  return <Line points={points} color={ACCENT} lineWidth={1.5} dashed dashSize={0.5} gapSize={0.3} transparent opacity={0.9} />;
}

/* Staged reveal after a generated layout arrives: modules rise into place, floor by floor, west to east. */
function RevealAnimator({ revealKey, order, refs }) {
  const { invalidate } = useThree();
  const start = useRef(null);
  useEffect(() => {
    if (!revealKey) return;
    start.current = performance.now();
    for (const id of order) { const g = refs.current.get(id); if (g) { g.scale.y = 0.001; g.position.y = 3; } }
    invalidate();
  }, [revealKey]);
  useFrame(() => {
    if (start.current == null) return;
    const t = (performance.now() - start.current) / 1000;
    let active = false;
    const step = Math.min(0.03, 1.2 / Math.max(1, order.length));
    order.forEach((id, i) => {
      const g = refs.current.get(id);
      if (!g) return;
      const p = Math.max(0, Math.min(1, (t - i * step) / 0.5));
      const e = 1 - Math.pow(1 - p, 3);
      g.scale.y = 0.001 + e * 0.999;
      g.position.y = (1 - e) * 3;
      if (p < 1) active = true;
    });
    if (active) invalidate();
    else {
      start.current = null;
      for (const id of order) { const g = refs.current.get(id); if (g) { g.scale.y = 1; g.position.y = 0; } }
      invalidate();
    }
  });
  return null;
}

/* Which world sides of module `m` are currently aligned with a snap guide → connection markers light up. */
function markersFor(m, t, guides) {
  if (!t) return null;
  const fp = footprint(t, m.rot);
  const pts = connectionPoints(m, t);
  if (!pts.length) return null;
  const near = (a, b) => Math.abs(a - b) < 0.05;
  return pts.map((p) => {
    let active = false;
    for (const g of guides || []) {
      if (g.axis === "x" && ((p.side === "west" && near(g.at, m.x)) || (p.side === "east" && near(g.at, m.x + fp.sx)))) active = true;
      if (g.axis === "z" && ((p.side === "north" && near(g.at, m.z)) || (p.side === "south" && near(g.at, m.z + fp.sz)))) active = true;
    }
    return { dx: p.dx, dz: p.dz, active };
  });
}

/**
 * The 3D/2D workspace. All editing intent is translated into editor actions – nothing lives only in the scene.
 */
export function RigScene({ editor, pendingType, onPlaced, onFeedback, className, onMeasure, onSiteStatus, sceneApi }) {
  const { modules, types, floorHeight, grid, activeFloor, view, mode, selection, conflicts, deliveryById, showLabels, fit, canEdit, select, clearSelection, beginDrag, dragTo, endDrag, addModule, setSnapGuides, snapGuides, snapEnabled, revealKey, site, siteEnabled, siteLayer, setSite, siteTool, env } = editor;
  const editable = canEdit && mode === "design";
  const moving = siteTool === "move" && editable && siteEnabled;
  const [hovered, setHovered] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [panning, setPanning] = useState(false);
  const [ghost, setGhost] = useState(null); // {typeId, x, z, floor, rot, guides, neighbors}
  const dragRef = useRef(null);
  const panRef = useRef(null);
  const apiRef = useRef(null);
  const controlsRef = useRef(null);
  const groupRefs = useRef(new Map());
  const tol = snapEnabled ? SNAP_TOLERANCE : 0;
  // «Ta bilde»: one frame rendered without editor chrome (view cube, metric grid), read back as PNG
  const shotRef = useRef(null);
  const [shooting, setShooting] = useState(false);
  const requestCapture = useCallback(() => new Promise((resolve, reject) => { shotRef.current = { resolve, reject }; setShooting(true); }), []);
  const onShotDone = useCallback(() => setShooting(false), []);

  const b = useMemo(() => boundsOf(modules, types), [modules, types]);
  const center = useMemo(() => [b.cx, Math.min(3, (b.floors * floorHeight) / 2), b.cz], [b, floorHeight]);
  const size = Math.max(16, b.size);
  // Sky & sun from the site's coordinates and the chosen local time/season/weather (deterministic, see rigSky.js)
  const sky = useMemo(() => resolveSky(env?.sky || DEFAULT_SKY, site && Number.isFinite(site.lat) ? { lat: site.lat, lng: site.lng } : undefined), [env?.sky, site?.lat, site?.lng]);
  // Rendering tier (see rigSky.QUALITIES): «lav» = plain shadows, «normal» = soft + contact shadows, «hoy» = + post-processing
  const quality = effectiveQuality(env?.quality);
  const hq = quality === "hoy", lq = quality === "lav";
  const is3D = view === "3D";
  // Ground-floor doors that face open air get an entrance landing with steps; doors facing a neighbour are internal.
  const entrances = useMemo(() => {
    const out = new Set();
    const ground = modules.filter((m) => { const t = types[m.module_type_id]; return t && m.floor <= 0 && m.floor + floorSpan(t) > 0; });
    for (const m of ground) {
      const t = types[m.module_type_id];
      if (m.floor !== 0) continue;
      const side = worldSide(t.door_side || "south", m.rot);
      const r = rect(m, t);
      const px = side === "east" ? r.x1 + 0.4 : side === "west" ? r.x0 - 0.4 : (r.x0 + r.x1) / 2;
      const pz = side === "south" ? r.z1 + 0.4 : side === "north" ? r.z0 - 0.4 : (r.z0 + r.z1) / 2;
      const blocked = ground.some((o) => {
        if (o.id === m.id) return false;
        const ro = rect(o, types[o.module_type_id]);
        return px > ro.x0 - 0.3 && px < ro.x1 + 0.3 && pz > ro.z0 - 0.3 && pz < ro.z1 + 0.3;
      });
      if (!blocked) out.add(m.id);
    }
    return out;
  }, [modules, types]);
  // The cameras' `position` prop must NOT follow the live bounds: r3f re-applies a changed prop on every render, so
  // dragging a module (which moves the bounding box) would snap the camera back to this default, far-out position.
  // Framing is CameraRig's job (on load, fit, view change) – the start position is frozen once per scene.
  const camStart = useRef(null);
  if (!camStart.current) camStart.current = { ortho: [b.cx, 250, b.cz], persp: [b.cx + size, size * 0.55, b.cz + size] };
  const targetFloor = activeFloor === "all" ? 0 : activeFloor;
  const revealOrder = useMemo(() => [...modules].sort((p, q) => p.floor - q.floor || p.x - q.x || p.z - q.z).map((m) => m.id), [revealKey]);

  // ---- module drag ----
  const onModulePointerDown = useCallback((m) => (e) => {
    if (e.button !== 0 && e.button !== undefined) return;
    if (moving) return; // the site is being positioned – modules are not interactive
    e.stopPropagation();
    if (pendingType) return;
    const toggle = e.shiftKey || e.metaKey || e.ctrlKey;
    if (toggle) { select(m.id, { toggle: true }); return; }
    const ids = selection.has(m.id) ? [...selection] : [m.id];
    if (!selection.has(m.id)) select(m.id);
    if (!editable) return;
    const y = m.floor * floorHeight;
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -y);
    const hit = new THREE.Vector3();
    if (!e.ray.intersectPlane(plane, hit)) return;
    const origin = {};
    for (const id of ids) {
      const mm = modules.find((x) => x.id === id);
      if (mm) origin[id] = { x: mm.x, z: mm.z };
    }
    dragRef.current = { plane, start: hit.clone(), origin, ids, primary: m.id, moved: false, y };
    beginDrag();
    setDragging(true);
  }, [pendingType, select, selection, editable, floorHeight, modules, beginDrag, moving]);

  // ---- Tomt-modus: slide the site under the rig ----
  const onSitePointerDown = useCallback((e) => {
    if (!moving || e.button !== 0) return;
    const p = apiRef.current?.toGround(e.clientX, e.clientY, 0);
    if (!p) return;
    panRef.current = { start: p, anchor: { x: site?.anchor_x || 0, z: site?.anchor_z || 0 } };
    setPanning(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [moving, site]);
  const onSitePointerMove = useCallback((e) => {
    const d = panRef.current;
    if (!d) return;
    const p = apiRef.current?.toGround(e.clientX, e.clientY, 0);
    if (!p) return;
    setSite({ anchor_x: r3(d.anchor.x + (p.x - d.start.x)), anchor_z: r3(d.anchor.z + (p.z - d.start.z)) });
  }, [setSite]);
  const onSitePointerUp = useCallback(() => { if (panRef.current) { panRef.current = null; setPanning(false); } }, []);
  useEffect(() => { if (!moving) { panRef.current = null; setPanning(false); } }, [moving]);

  const onDragMove = useCallback((hit) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = hit.x - d.start.x;
    const dz = hit.z - d.start.z;
    if (!d.moved && Math.hypot(dx, dz) < 0.05) return;
    d.moved = true;
    const prim = modules.find((x) => x.id === d.primary);
    const t = prim && types[prim.module_type_id];
    if (!prim || !t) return;
    const o = d.origin[d.primary];
    const others = modules.filter((x) => !d.ids.includes(x.id));
    const snapped = snapPosition(o.x + dx, o.z + dz, footprint(t, prim.rot), prim, others, types, grid, tol);
    const sdx = snapped.x - o.x;
    const sdz = snapped.z - o.z;
    const positions = {};
    for (const id of d.ids) positions[id] = { x: r3(d.origin[id].x + sdx), z: r3(d.origin[id].z + sdz) };
    dragTo(positions);
    setSnapGuides(snapped.guides.length ? { guides: snapped.guides, neighbors: snapped.neighbors.map((n) => n.label || n.id), y: d.y, moduleId: d.primary } : null);
  }, [modules, types, grid, tol, dragTo, setSnapGuides]);

  const onDragEnd = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null;
    setDragging(false);
    setSnapGuides(null);
    endDrag(!!d?.moved);
  }, [endDrag, setSnapGuides]);

  // ---- placement (click a library item, then click in the scene) and HTML5 drag & drop from the library ----
  const ghostFor = useCallback((clientX, clientY, typeId, rot = 0) => {
    const t = types[typeId];
    const api = apiRef.current;
    if (!t || !api) return null;
    const p = api.toGround(clientX, clientY, targetFloor * floorHeight);
    if (!p) return null;
    const fp = footprint(t, rot);
    const probe = { id: "__ghost", module_type_id: typeId, floor: targetFloor };
    const s = snapPosition(p.x - fp.sx / 2, p.z - fp.sz / 2, fp, probe, modules, types, grid, tol);
    return { typeId, x: s.x, z: s.z, floor: targetFloor, rot, guides: s.guides, neighbors: s.neighbors.map((n) => n.label || n.id) };
  }, [types, targetFloor, floorHeight, modules, grid, tol]);

  const place = useCallback((g) => {
    const t = types[g.typeId];
    const label = nextLabel(modules, t);
    addModule(g.typeId, { x: g.x, z: g.z, floor: g.floor, rot: g.rot, snap: false });
    onFeedback?.(`${label} plassert på etasje ${g.floor + 1}${g.neighbors?.length ? ` · snappet til ${g.neighbors.slice(0, 2).join(" og ")}` : ""}`);
  }, [types, modules, addModule, onFeedback]);

  const onContainerPointerMove = useCallback((e) => {
    if (!pendingType || !editable) return;
    setGhost((g) => ghostFor(e.clientX, e.clientY, pendingType, g?.rot || 0));
  }, [pendingType, editable, ghostFor]);
  const onContainerClick = useCallback((e) => {
    if (!pendingType || !editable) return;
    const g = ghostFor(e.clientX, e.clientY, pendingType, ghost?.rot || 0);
    if (!g) return;
    place(g);
    if (!e.shiftKey) { setGhost(null); onPlaced?.(); }
  }, [pendingType, editable, ghostFor, ghost, place, onPlaced]);
  const onDragOver = useCallback((e) => {
    if (!editable) return;
    const typeId = dragState.typeId;
    if (!typeId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setGhost(ghostFor(e.clientX, e.clientY, typeId, ghost?.rot || 0));
  }, [editable, ghost, ghostFor]);
  const onDrop = useCallback((e) => {
    if (!editable) return;
    const typeId = e.dataTransfer.getData("application/x-module-type") || dragState.typeId;
    dragState.typeId = null;
    if (!typeId) return;
    e.preventDefault();
    const g = ghostFor(e.clientX, e.clientY, typeId, ghost?.rot || 0);
    setGhost(null);
    if (g) place(g);
  }, [editable, ghost, ghostFor, place]);
  useEffect(() => { if (!pendingType) setGhost(null); }, [pendingType]);
  useEffect(() => {
    if (!pendingType) return;
    const onKey = (e) => { if (e.key === "r" || e.key === "R") setGhost((g) => (g ? { ...g, rot: (g.rot + 90) % 360 } : g)); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingType]);

  const cursor = moving ? (panning ? "grabbing" : "move") : pendingType ? "copy" : dragging ? "grabbing" : hovered && editable ? "grab" : "default";
  const hasSelection = selection.size > 0;
  const guideY = snapGuides?.y ?? (ghost ? ghost.floor * floorHeight : 0);
  const guides = snapGuides?.guides || ghost?.guides || [];
  const primaryDrag = snapGuides?.moduleId ? modules.find((m) => m.id === snapGuides.moduleId) : null;
  const ghostType = ghost ? types[ghost.typeId] : null;
  const ghostFp = ghostType ? footprint(ghostType, ghost.rot) : null;
  const showMarkers = mode === "design" && (selection.size === 1 || dragging);

  return (
    <div className={"relative h-full w-full select-none " + (className || "")} style={{ cursor }} onPointerMove={(e) => { onContainerPointerMove(e); onSitePointerMove(e); }} onPointerDown={onSitePointerDown} onPointerUp={onSitePointerUp} onPointerCancel={onSitePointerUp} onClick={onContainerClick} onDragOver={onDragOver} onDrop={onDrop} onDragLeave={() => { if (!pendingType) setGhost(null); }} data-testid="rig-scene">
      <Canvas shadows dpr={[1, lq ? 1 : 1.5]} frameloop="demand" gl={{ antialias: true, powerPreference: "high-performance", toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.0 }} onPointerMissed={(e) => { if (e.button === 0 && !pendingType && !moving) clearSelection(); }} style={{ background: SCENE_BG }}>
        <PhotoMapsProvider>
        {!lq && <SoftShadows size={sky.shadowSize} samples={hq ? 17 : 12} focus={0.4} />}
        {view === "2D" ? <OrthographicCamera makeDefault near={0.1} far={1000} position={camStart.current.ortho} /> : <PerspectiveCamera makeDefault fov={38} near={0.5} far={3000} position={camStart.current.persp} />}
        <OrbitControls ref={controlsRef} makeDefault enabled={!dragging && !pendingType && !moving} enableRotate={view === "3D"} enableDamping dampingFactor={0.12} maxPolarAngle={Math.PI / 2 - 0.04} minDistance={4} maxDistance={siteEnabled ? 1400 : 700} screenSpacePanning={view === "2D"} mouseButtons={{ LEFT: view === "2D" ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN }} />
        <CameraRig fit={fit} view={view} modules={modules} types={types} floorHeight={floorHeight} controlsRef={controlsRef} />
        <SceneApi apiRef={apiRef} sceneApi={sceneApi} requestCapture={requestCapture} />
        <CaptureRunner shooting={shooting} shotRef={shotRef} onDone={onShotDone} />
        <DragController dragRef={dragRef} onMove={onDragMove} onEnd={onDragEnd} />
        {onMeasure && <ScaleProbe center={center} onMeasure={onMeasure} />}
        <Lights center={center} size={size} shadows={is3D} sky={sky} background={is3D} mapSize={lq ? 1024 : 2048} />
        {is3D && <RigEffects enabled={hq} isNight={sky.isNight} />}

        {/* ground: real site tiles in Tomt-modus, otherwise natural terrain + a metric grid (1 m cells, 10 m sections) that fades with distance */}
        {siteEnabled ? (
          <>
            <fog attach="fog" args={[is3D ? sky.fog : SCENE_BG, is3D ? 380 : 420, is3D ? 1700 : 1900]} />
            <SiteGround site={site} layer={siteLayer} onStatus={onSiteStatus} />
          </>
        ) : (
          <>
            {is3D && <fog attach="fog" args={[sky.fog, 260, 1500]} />}
            {is3D && <Terrain center={[b.cx, b.cz]} night={sky.isNight} />}
            <Grid visible={!shooting} position={[0, -0.005, 0]} args={[10, 10]} cellSize={1} cellThickness={0.5} cellColor={is3D ? "#a7ada6" : "#dfe3e8"} sectionSize={10} sectionThickness={0.9} sectionColor={is3D ? "#8f958e" : "#c6cdd6"} fadeDistance={is3D ? 220 : 280} fadeStrength={1.5} infiniteGrid followCamera={false} />
          </>
        )}
        {/* rig footprint while the site is being positioned – makes "where does the rig land" explicit */}
        {moving && modules.length > 0 && (
          <Line points={[[b.x0, 0.06, b.z0], [b.x1, 0.06, b.z0], [b.x1, 0.06, b.z1], [b.x0, 0.06, b.z1], [b.x0, 0.06, b.z0]]} color={ACCENT} lineWidth={1.5} dashed dashSize={1} gapSize={0.6} transparent opacity={0.95} />
        )}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
          <planeGeometry args={[1600, 1600]} />
          <shadowMaterial transparent opacity={siteEnabled ? 0.22 : 0.13} />
        </mesh>
        {/* omgivelser: rig pad, site fence, scale references, joint flashings */}
        <Surroundings modules={modules} types={types} floorHeight={floorHeight} bounds={b} env={env} view={view} />
        {view === "3D" && !lq && <ContactShadows position={[b.cx, 0.002, b.cz]} scale={[size * 1.6, size * 1.6]} blur={1.3} far={7} opacity={siteEnabled ? 0.5 : 0.42} resolution={1024} color="#151c26" frames={Infinity} />}

        {modules.map((m) => {
          const t = types[m.module_type_id];
          if (!t) return null;
          const dimmed = activeFloor !== "all" && m.floor !== activeFloor && !(m.floor < activeFloor && m.floor + (t.floors_span || 1) > activeFloor);
          const isSel = selection.has(m.id);
          const status = deliveryById[m.id]?.delivery_status || m.delivery_status || "ikke_tildelt";
          let color = mode === "delivery" ? DELIVERY_COLORS[status] || DELIVERY_COLORS.ikke_tildelt : t.color;
          if (hasSelection && !isSel && !dimmed) color = shade(color, 0.1);
          const isHover = hovered === m.id;
          const label = isSel ? { title: m.label || t.code, sub: `${t.name}${t.beds ? ` · ${t.beds} senger` : ""}` } : isHover ? { title: m.label || t.code, sub: t.name } : view === "2D" || showLabels ? { title: m.label || t.code } : null;
          const markers = showMarkers && isSel ? markersFor(m, t, snapGuides?.moduleId === m.id ? snapGuides.guides : []) : null;
          return (
            <ModuleMesh key={m.id} ref={(el) => (el ? groupRefs.current.set(m.id, el) : groupRefs.current.delete(m.id))} m={m} type={t} floorHeight={floorHeight} color={color} dimmed={dimmed} selected={isSel} hovered={isHover} conflict={mode === "design" && conflicts.has(m.id)}
              entrance={entrances.has(m.id)} night={sky.lightsOn && is3D} label={label} compactLabel={!isSel && !isHover} markers={markers}
              onPointerDown={onModulePointerDown(m)} onPointerOver={(e) => { e.stopPropagation(); setHovered(m.id); }} onPointerOut={() => setHovered((h) => (h === m.id ? null : h))} />
          );
        })}

        {ghost && ghostType && (
          <>
            <ModuleMesh m={{ id: "__ghost", ...ghost, module_type_id: ghost.typeId }} type={ghostType} floorHeight={floorHeight} color={ghostType.color} dimmed={false} selected={false} hovered={false} conflict={false} label={null} ghost markers={markersFor({ ...ghost, module_type_id: ghost.typeId }, ghostType, ghost.guides)} />
            <Html position={[ghost.x + ghostFp.sx / 2, ghost.floor * floorHeight + 0.05, ghost.z + ghostFp.sz + 0.8]} center zIndexRange={[30, 0]} style={{ pointerEvents: "none" }}>
              <div className="whitespace-nowrap rounded-md bg-background/95 px-2 py-0.5 font-mono text-[11px] tabular text-foreground shadow-pop" data-testid="rig-ghost-dims">{fmt1(ghostFp.sx)} × {fmt1(ghostFp.sz)} m{ghost.neighbors?.length ? <span className="text-primary"> · {ghost.neighbors[0]}</span> : null}</div>
            </Html>
          </>
        )}
        {guides.map((g, i) => <SnapGuide key={i} g={g} y={guideY} />)}
        {primaryDrag && snapGuides?.neighbors?.length > 0 && (
          <Html position={[primaryDrag.x + footprint(types[primaryDrag.module_type_id], primaryDrag.rot).sx / 2, guideY + 0.2, primaryDrag.z + footprint(types[primaryDrag.module_type_id], primaryDrag.rot).sz + 0.8]} center zIndexRange={[30, 0]} style={{ pointerEvents: "none" }}>
            <div className="whitespace-nowrap rounded-md bg-background/95 px-2 py-0.5 text-[11px] text-primary shadow-pop" data-testid="rig-snap-badge">Snappet til {snapGuides.neighbors.slice(0, 2).join(" og ")}</div>
          </Html>
        )}

        {view === "3D" && !shooting && (
          <GizmoHelper alignment="top-right" margin={[60, 60]}>
            <GizmoViewcube faces={["Ø", "V", "Topp", "Bunn", "S", "N"]} color="#f8f9fb" hoverColor="#dbe4f7" textColor="#475467" strokeColor="#cdd3dc" font="600 30px Inter, ui-sans-serif, sans-serif" opacity={0.97} />
          </GizmoHelper>
        )}
        <RevealAnimator revealKey={revealKey} order={revealOrder} refs={groupRefs} />
        </PhotoMapsProvider>
      </Canvas>
    </div>
  );
}
